// Wall-hack, shown on the ARENA WALLS instead of as a ring on the worm. When wall-hack
// is active the map border animates (glows / opens / shimmers) to say "the walls are
// passable." Pure canvas, drawn in PIXELS so the same code serves the /wh gallery and
// the in-game map renderer (which passes the border geometry * scale). Works on both
// rectangle, circle and stadium arenas. Switch in-game with ?wh=1..20.

import { readVariantParam } from "../shared/variant-param";
export type WallGeo =
  | { type: "rectangle"; x: number; y: number; w: number; h: number; bw: number }
  | { type: "circle"; cx: number; cy: number; radius: number; bw: number }
  // a pill lying along x: every point within `radius` of the segment cx +- hs
  | { type: "stadium"; cx: number; cy: number; hs: number; radius: number; bw: number };

const TAU = Math.PI * 2;

// A stadium's outline at radius r, as a closed sub-path (ccw: the other way
// round, for cutting a hole with the default nonzero fill).
function pill(ctx: CanvasRenderingContext2D, cx: number, cy: number, hs: number, r: number, ccw = false): void {
  r = Math.max(0, r);
  const l = cx - hs, rt = cx + hs;
  if (!ccw) { ctx.moveTo(l, cy - r); ctx.lineTo(rt, cy - r); ctx.arc(rt, cy, r, -Math.PI / 2, Math.PI / 2); ctx.lineTo(l, cy + r); ctx.arc(l, cy, r, Math.PI / 2, Math.PI * 1.5); }
  else { ctx.moveTo(l, cy - r); ctx.arc(l, cy, r, Math.PI * 1.5, Math.PI / 2, true); ctx.lineTo(rt, cy + r); ctx.arc(rt, cy, r, Math.PI / 2, -Math.PI / 2, true); ctx.lineTo(l, cy - r); }
  ctx.closePath();
}

function rgba(hex: string, a: number): string {
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const n = parseInt(h, 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}
const fract = (x: number): number => x - Math.floor(x);

// A path parallel to the inner wall edge, offset by `off` px: off>0 moves INWARD (into
// the play area, smaller), off<0 moves OUTWARD (into the wall band, bigger).
function edgePath(ctx: CanvasRenderingContext2D, g: WallGeo, off: number): void {
  ctx.beginPath();
  if (g.type === "rectangle") ctx.rect(g.x + off, g.y + off, g.w - 2 * off, g.h - 2 * off);
  else if (g.type === "stadium") pill(ctx, g.cx, g.cy, g.hs, g.radius - off);
  else ctx.arc(g.cx, g.cy, Math.max(0, g.radius - off), 0, TAU);
}
// A ring-shaped sub-path between two inward offsets (offA nearer the wall, offB more
// inward), for filling a band with fill("evenodd"). Both offsets are inward-positive.
function edgeRegion(ctx: CanvasRenderingContext2D, g: WallGeo, offA: number, offB: number): void {
  ctx.beginPath();
  if (g.type === "rectangle") {
    ctx.rect(g.x + offA, g.y + offA, g.w - 2 * offA, g.h - 2 * offA);
    ctx.rect(g.x + offB, g.y + offB, g.w - 2 * offB, g.h - 2 * offB);
  } else if (g.type === "stadium") {
    pill(ctx, g.cx, g.cy, g.hs, g.radius - offA);
    pill(ctx, g.cx, g.cy, g.hs, g.radius - offB);
  } else {
    ctx.arc(g.cx, g.cy, Math.max(0, g.radius - offA), 0, TAU);
    ctx.arc(g.cx, g.cy, Math.max(0, g.radius - offB), 0, TAU);
  }
}
// Clip to the wall band (outer edge minus inner edge).
function bandClip(ctx: CanvasRenderingContext2D, g: WallGeo): void {
  ctx.beginPath();
  if (g.type === "rectangle") {
    ctx.rect(g.x - g.bw, g.y - g.bw, g.w + 2 * g.bw, g.h + 2 * g.bw);
    ctx.rect(g.x, g.y, g.w, g.h);
  } else if (g.type === "stadium") {
    pill(ctx, g.cx, g.cy, g.hs, g.radius + g.bw);
    pill(ctx, g.cx, g.cy, g.hs, g.radius);
  } else {
    ctx.arc(g.cx, g.cy, g.radius + g.bw, 0, TAU);
    ctx.arc(g.cx, g.cy, g.radius, 0, TAU);
  }
  ctx.clip("evenodd");
}
function bounds(g: WallGeo): { l: number; t: number; r: number; b: number } {
  if (g.type === "rectangle") return { l: g.x - g.bw, t: g.y - g.bw, r: g.x + g.w + g.bw, b: g.y + g.h + g.bw };
  if (g.type === "stadium") return { l: g.cx - g.hs - g.radius - g.bw, t: g.cy - g.radius - g.bw, r: g.cx + g.hs + g.radius + g.bw, b: g.cy + g.radius + g.bw };
  return { l: g.cx - g.radius - g.bw, t: g.cy - g.radius - g.bw, r: g.cx + g.radius + g.bw, b: g.cy + g.radius + g.bw };
}
function fillBand(ctx: CanvasRenderingContext2D, g: WallGeo, style: string): void {
  const b = bounds(g);
  ctx.save(); bandClip(ctx, g); ctx.fillStyle = style; ctx.fillRect(b.l, b.t, b.r - b.l, b.b - b.t); ctx.restore();
}
function perimeter(g: WallGeo): number {
  if (g.type === "stadium") return 4 * g.hs + TAU * g.radius;
  return g.type === "rectangle" ? 2 * (g.w + g.h) : TAU * g.radius;
}
// Point at arc-length s along the inner edge, with an inward-pointing unit normal.
function pointAt(g: WallGeo, s: number): { x: number; y: number; nx: number; ny: number } {
  if (g.type === "circle") {
    const a = s / g.radius - Math.PI / 2;
    return { x: g.cx + Math.cos(a) * g.radius, y: g.cy + Math.sin(a) * g.radius, nx: -Math.cos(a), ny: -Math.sin(a) };
  }
  if (g.type === "stadium") {
    // from the top straight's left end, clockwise: top, right end, bottom, left end
    const st = 2 * g.hs, arc = Math.PI * g.radius, per = 2 * st + 2 * arc;
    let d = ((s % per) + per) % per;
    if (d < st) return { x: g.cx - g.hs + d, y: g.cy - g.radius, nx: 0, ny: 1 }; d -= st;
    if (d < arc) { const a = -Math.PI / 2 + d / g.radius; return { x: g.cx + g.hs + Math.cos(a) * g.radius, y: g.cy + Math.sin(a) * g.radius, nx: -Math.cos(a), ny: -Math.sin(a) }; } d -= arc;
    if (d < st) return { x: g.cx + g.hs - d, y: g.cy + g.radius, nx: 0, ny: -1 }; d -= st;
    const a = Math.PI / 2 + d / g.radius;
    return { x: g.cx - g.hs + Math.cos(a) * g.radius, y: g.cy + Math.sin(a) * g.radius, nx: -Math.cos(a), ny: -Math.sin(a) };
  }
  const per = perimeter(g);
  let d = ((s % per) + per) % per;
  const { x, y, w, h } = g;
  if (d < w) return { x: x + d, y, nx: 0, ny: 1 }; d -= w;
  if (d < h) return { x: x + w, y: y + d, nx: -1, ny: 0 }; d -= h;
  if (d < w) return { x: x + w - d, y: y + h, nx: 0, ny: -1 }; d -= w;
  return { x, y: y + h - d, nx: 1, ny: 0 };
}

// Stroke a thick line along the wall band from perimeter s0 to s1 (for perimeter-meter
// style countdowns). `off` is the inward offset of the line centre (band centre ~ -bw/2).
function wallStroke(ctx: CanvasRenderingContext2D, g: WallGeo, s0: number, s1: number, off: number, lw: number, style: string): void {
  if (s1 <= s0) return;
  ctx.save(); ctx.lineWidth = lw; ctx.strokeStyle = style; ctx.lineCap = "butt"; ctx.lineJoin = "round";
  const steps = Math.max(2, Math.ceil((s1 - s0) / Math.max(2, lw * 0.5)));
  ctx.beginPath();
  for (let i = 0; i <= steps; i++) { const s = s0 + (s1 - s0) * (i / steps); const p = pointAt(g, s); ctx.lineTo(p.x + p.nx * off, p.y + p.ny * off); }
  ctx.stroke(); ctx.restore();
}

export const WALLHACK_VARIANTS: { n: number; label: string }[] = [
  { n: 1, label: "Neon glow (pulsing)" },
  { n: 2, label: "Inner edge line" },
  { n: 3, label: "Marching ants" },
  { n: 4, label: "Shimmer sweep" },
  { n: 5, label: "Passable gaps" },
  { n: 6, label: "Energized band (breathing)" },
  { n: 7, label: "Scanline field" },
  { n: 8, label: "Tron double line" },
  { n: 9, label: "Corner brackets" },
  { n: 10, label: "Electric arcs" },
  { n: 11, label: "Inward ripples" },
  { n: 12, label: "Dissolve flicker" },
  { n: 13, label: "Portal shimmer" },
  { n: 14, label: "Dotted march" },
  { n: 15, label: "Gradient energy fill" },
  { n: 16, label: "Grid overlay" },
  { n: 17, label: "Rising particles" },
  { n: 18, label: "Breathing thickness" },
  { n: 19, label: "White sweep over glow" },
  { n: 20, label: "Hazard stripes" },
  // --- "be creative": blink / recolour / cycle / fade / flash ---
  { n: 21, label: "Blinking wall" },
  { n: 22, label: "Solid recolour" },
  { n: 23, label: "Colour cycle" },
  { n: 24, label: "Walls fade away" },
  { n: 25, label: "Checkerboard flash" },
  // --- variations of #24 (walls opening up) ---
  { n: 26, label: "Fade out (full vanish)" },
  { n: 27, label: "Fade from inside out" },
  { n: 28, label: "Dissolving patches" },
  { n: 29, label: "Opening sweep (moving gap)" },
  { n: 30, label: "Drifting gaps" },
  { n: 31, label: "Fade + edge glow" },
  { n: 32, label: "Draining green band (countdown)" },
  // --- variations of the countdown band (all drain with the effect) ---
  { n: 33, label: "Countdown - gradient fill" },
  { n: 34, label: "Countdown - front line only" },
  { n: 35, label: "Countdown - double front line" },
  { n: 36, label: "Countdown - opacity fade" },
  { n: 37, label: "Countdown - segmented meter" },
  { n: 38, label: "Countdown - perimeter wipe" },
  { n: 39, label: "Countdown - recede + glow" },
  { n: 40, label: "Countdown - striped fill" },
  { n: 41, label: "Countdown - dotted fill" },
  { n: 42, label: "Countdown - reverse (gap at wall)" },
  { n: 43, label: "Countdown - feathered front" },
  { n: 44, label: "Countdown - recede + pulse" },
  { n: 45, label: "Countdown - two-tone band" },
  { n: 46, label: "Countdown - recede + fade" },
  { n: 47, label: "Countdown - hollow outline" },
  { n: 48, label: "Countdown - colour shift (green->amber)" },
  { n: 49, label: "Countdown - accelerating blink" },
  { n: 50, label: "Countdown - marching front" },
  { n: 51, label: "Countdown - ticked band" },
  { n: 52, label: "Countdown - subtle" },
];

// Draw the wall-hack wall effect. g is the border geometry in PIXELS, t seconds, col hex.
// `frac` is the effect's remaining time (1 -> 0); used by countdown-style designs (32).
export function drawWallHackWalls(ctx: CanvasRenderingContext2D, g: WallGeo, variant: number, t: number, col: string, frac: number = 1): void {
  const bw = g.bw;
  const per = perimeter(g);
  ctx.save();
  ctx.lineJoin = "round"; ctx.lineCap = "round";

  switch (variant) {
    case 1: { // Neon glow: bright inner-edge stroke with a pulsing bloom.
      const s = 0.5 + 0.5 * Math.sin(t * 3);
      ctx.lineWidth = Math.max(2, bw * 0.5); ctx.strokeStyle = col;
      ctx.shadowBlur = bw * (0.8 + 2 * s); ctx.shadowColor = col;
      edgePath(ctx, g, bw * 0.4); ctx.stroke();
      break;
    }
    case 2: { // A crisp bright line hugging the inner edge, gently pulsing.
      ctx.lineWidth = Math.max(1.5, bw * 0.2); ctx.strokeStyle = col;
      ctx.globalAlpha = 0.55 + 0.45 * (0.5 + 0.5 * Math.sin(t * 4));
      edgePath(ctx, g, bw * 0.12); ctx.stroke();
      break;
    }
    case 3: { // Marching ants scrolling around the inner edge.
      ctx.lineWidth = Math.max(1.5, bw * 0.32); ctx.strokeStyle = col;
      ctx.setLineDash([bw * 1.2, bw * 0.9]); ctx.lineDashOffset = -t * bw * 3;
      edgePath(ctx, g, bw * 0.4); ctx.stroke();
      break;
    }
    case 4: { // A bright glow segment sweeps around the perimeter.
      ctx.lineWidth = Math.max(1, bw * 0.14); ctx.strokeStyle = rgba(col, 0.35);
      edgePath(ctx, g, bw * 0.35); ctx.stroke();
      const s0 = (t * per * 0.3) % per;
      ctx.shadowBlur = bw; ctx.shadowColor = col;
      for (let k = -7; k <= 7; k++) {
        const p = pointAt(g, s0 + k * bw * 0.55);
        const a = 1 - Math.abs(k) / 8;
        ctx.fillStyle = rgba(col, a * 0.9);
        ctx.beginPath(); ctx.arc(p.x, p.y, bw * 0.32, 0, TAU); ctx.fill();
      }
      break;
    }
    case 5: { // Thick dashed segments with big gaps: the wall looks broken open.
      ctx.lineWidth = bw * 0.8; ctx.strokeStyle = rgba(col, 0.7);
      ctx.setLineDash([bw * 2.5, bw * 2.5]); ctx.lineDashOffset = -t * bw * 2;
      edgePath(ctx, g, bw * 0.5); ctx.stroke();
      break;
    }
    case 6: { // The whole wall band glows and breathes.
      const s = 0.5 + 0.5 * Math.sin(t * 2);
      fillBand(ctx, g, rgba(col, 0.12 + 0.22 * s));
      ctx.lineWidth = Math.max(1.5, bw * 0.16); ctx.strokeStyle = rgba(col, 0.6 + 0.4 * s);
      edgePath(ctx, g, bw * 0.1); ctx.stroke();
      break;
    }
    case 7: { // Scanlines drift across the wall band.
      const b = bounds(g);
      ctx.save(); bandClip(ctx, g);
      const step = Math.max(4, bw * 0.5), off = (t * 30) % step;
      ctx.fillStyle = rgba(col, 0.3);
      for (let y = b.t + off; y < b.b; y += step) ctx.fillRect(b.l, y, b.r - b.l, Math.max(1, bw * 0.1));
      ctx.restore();
      break;
    }
    case 8: { // Two bright parallel lines (tron).
      const s = 0.6 + 0.4 * Math.sin(t * 3);
      ctx.strokeStyle = rgba(col, s); ctx.lineWidth = Math.max(1, bw * 0.13);
      edgePath(ctx, g, bw * 0.2); ctx.stroke();
      edgePath(ctx, g, bw * 0.6); ctx.stroke();
      break;
    }
    case 9: { // Pulsing brackets: rectangle corners, or N/E/S/W arc ticks on a circle.
      const s = 0.5 + 0.5 * Math.sin(t * 4);
      ctx.strokeStyle = rgba(col, 0.5 + 0.5 * s); ctx.lineWidth = Math.max(2, bw * 0.4);
      ctx.shadowBlur = bw * s; ctx.shadowColor = col;
      if (g.type === "rectangle") {
        const len = Math.min(g.w, g.h) * 0.18, o = bw * 0.3;
        const cor: [number, number, number, number][] = [
          [g.x - o, g.y - o, 1, 1], [g.x + g.w + o, g.y - o, -1, 1],
          [g.x + g.w + o, g.y + g.h + o, -1, -1], [g.x - o, g.y + g.h + o, 1, -1],
        ];
        cor.forEach(([cx, cy, sx, sy]) => {
          ctx.beginPath(); ctx.moveTo(cx + sx * len, cy); ctx.lineTo(cx, cy); ctx.lineTo(cx, cy + sy * len); ctx.stroke();
        });
      } else if (g.type === "stadium") {
        // ticks at each end's tip and at the middle of both straights
        const r = g.radius + bw * 0.5, arc = 0.28;
        ctx.beginPath(); ctx.arc(g.cx + g.hs, g.cy, r, -arc, arc); ctx.stroke();
        ctx.beginPath(); ctx.arc(g.cx - g.hs, g.cy, r, Math.PI - arc, Math.PI + arc); ctx.stroke();
        const len = r * arc;
        ctx.beginPath(); ctx.moveTo(g.cx - len, g.cy - r); ctx.lineTo(g.cx + len, g.cy - r); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(g.cx - len, g.cy + r); ctx.lineTo(g.cx + len, g.cy + r); ctx.stroke();
      } else {
        const r = g.radius + bw * 0.5, arc = 0.28;
        for (let i = 0; i < 4; i++) { const a = -Math.PI / 2 + i * (TAU / 4); ctx.beginPath(); ctx.arc(g.cx, g.cy, r, a - arc, a + arc); ctx.stroke(); }
      }
      break;
    }
    case 10: { // Jagged electric arc running along the wall.
      const N = Math.max(48, Math.floor(per / 6));
      ctx.beginPath();
      for (let i = 0; i <= N; i++) {
        const s = (i / N) * per, p = pointAt(g, s);
        const j = (Math.sin(s * 0.5 + t * 22) + Math.sin(s * 0.17 + t * 13)) * bw * 0.22;
        const x = p.x + p.nx * j, y = p.y + p.ny * j;
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.strokeStyle = col; ctx.lineWidth = Math.max(1, bw * 0.14); ctx.shadowBlur = bw * 0.6; ctx.shadowColor = col; ctx.stroke();
      break;
    }
    case 11: { // Ripples pulse inward from the wall.
      ctx.lineWidth = Math.max(1.5, bw * 0.18); ctx.strokeStyle = col;
      for (let k = 0; k < 3; k++) {
        const p = ((t * 0.7 + k / 3) % 1);
        ctx.globalAlpha = (1 - p) * 0.55;
        edgePath(ctx, g, p * bw * 3); ctx.stroke();
      }
      break;
    }
    case 12: { // The wall band flickers as if dissolving.
      const a = 0.08 + 0.28 * Math.abs(Math.sin(t * 9) * 0.6 + Math.sin(t * 6.3) * 0.4);
      fillBand(ctx, g, rgba(col, a));
      break;
    }
    case 13: { // Soft portal shimmer: a glowing inner edge with a slow bright drift.
      ctx.lineWidth = Math.max(2, bw * 0.4); ctx.strokeStyle = rgba(col, 0.5);
      ctx.shadowBlur = bw * 1.2; ctx.shadowColor = col;
      edgePath(ctx, g, bw * 0.35); ctx.stroke();
      const s0 = (t * per * 0.12) % per;
      for (let k = -18; k <= 18; k++) {
        const p = pointAt(g, s0 + k * bw * 0.5), a = 1 - Math.abs(k) / 19;
        ctx.fillStyle = rgba("#ffffff", a * 0.5);
        ctx.beginPath(); ctx.arc(p.x, p.y, bw * 0.2, 0, TAU); ctx.fill();
      }
      break;
    }
    case 14: { // A ring of dots marching around the inner edge.
      ctx.lineWidth = Math.max(2, bw * 0.5); ctx.strokeStyle = col; ctx.lineCap = "round";
      ctx.setLineDash([1, bw * 1.1]); ctx.lineDashOffset = -t * bw * 2.5;
      edgePath(ctx, g, bw * 0.4); ctx.stroke();
      break;
    }
    case 15: { // The band fills with a colour that fades from the outer to the inner edge.
      const s = 0.4 + 0.3 * (0.5 + 0.5 * Math.sin(t * 2));
      ctx.save(); bandClip(ctx, g);
      for (let i = 0; i < 5; i++) {
        edgePath(ctx, g, (i / 5) * bw);
        ctx.fillStyle = rgba(col, (s * (1 - i / 5)) * 0.35);
        ctx.fill();
      }
      ctx.restore();
      break;
    }
    case 16: { // A faint grid glows across the wall band.
      const b = bounds(g);
      ctx.save(); bandClip(ctx, g);
      const step = Math.max(5, bw * 0.6), a = 0.15 + 0.2 * (0.5 + 0.5 * Math.sin(t * 3));
      ctx.strokeStyle = rgba(col, a); ctx.lineWidth = 1;
      for (let x = b.l; x < b.r; x += step) { ctx.beginPath(); ctx.moveTo(x, b.t); ctx.lineTo(x, b.b); ctx.stroke(); }
      for (let y = b.t; y < b.b; y += step) { ctx.beginPath(); ctx.moveTo(b.l, y); ctx.lineTo(b.r, y); ctx.stroke(); }
      ctx.restore();
      break;
    }
    case 17: { // Little particles rise off the wall into the arena.
      const count = Math.max(24, Math.floor(per / 22));
      for (let i = 0; i < count; i++) {
        const s = (i / count) * per + fract(Math.sin(i * 12.9) * 43758.5) * (per / count);
        const ph = (t * 0.5 + fract(Math.sin(i * 7.7) * 1234.5)) % 1;
        const p = pointAt(g, s), off = ph * bw * 3.2;
        ctx.fillStyle = rgba(col, (1 - ph) * 0.8);
        ctx.beginPath(); ctx.arc(p.x + p.nx * off, p.y + p.ny * off, Math.max(1, bw * 0.12), 0, TAU); ctx.fill();
      }
      break;
    }
    case 18: { // A bright inner line whose thickness breathes.
      const s = 0.5 + 0.5 * Math.sin(t * 3);
      ctx.lineWidth = bw * (0.12 + 0.55 * s); ctx.strokeStyle = col;
      ctx.shadowBlur = bw * 0.4 * s; ctx.shadowColor = col;
      edgePath(ctx, g, bw * 0.3); ctx.stroke();
      break;
    }
    case 19: { // Coloured glow with a crisp white highlight sweeping over it.
      ctx.lineWidth = Math.max(2, bw * 0.42); ctx.strokeStyle = rgba(col, 0.55);
      ctx.shadowBlur = bw * 0.8; ctx.shadowColor = col;
      edgePath(ctx, g, bw * 0.35); ctx.stroke();
      ctx.shadowBlur = 0;
      const s0 = (t * per * 0.4) % per;
      for (let k = -5; k <= 5; k++) {
        const p = pointAt(g, s0 + k * bw * 0.5), a = 1 - Math.abs(k) / 6;
        ctx.fillStyle = rgba("#ffffff", a * 0.9);
        ctx.beginPath(); ctx.arc(p.x, p.y, bw * 0.26, 0, TAU); ctx.fill();
      }
      break;
    }
    case 20: { // Diagonal hazard stripes scroll along the wall band.
      const b = bounds(g);
      ctx.save(); bandClip(ctx, g);
      const step = Math.max(6, bw * 1.1), off = (t * 22) % (step * 2);
      ctx.strokeStyle = rgba(col, 0.5); ctx.lineWidth = step * 0.6;
      const span = b.b - b.t;
      for (let d = b.l - span; d < b.r + span; d += step * 2) {
        const x0 = d + off;
        ctx.beginPath(); ctx.moveTo(x0, b.t); ctx.lineTo(x0 - span, b.b); ctx.stroke();
      }
      ctx.restore();
      break;
    }
    case 21: { // The whole wall blinks the colour on and off.
      if (Math.sin(t * 6) > 0) {
        fillBand(ctx, g, rgba(col, 0.5));
        ctx.lineWidth = Math.max(2, bw * 0.4); ctx.strokeStyle = col;
        edgePath(ctx, g, bw * 0.2); ctx.stroke();
      }
      break;
    }
    case 22: { // The wall simply recolours to the wall-hack colour (steady, faint pulse).
      const s = 0.5 + 0.5 * Math.sin(t * 2.5);
      fillBand(ctx, g, rgba(col, 0.75 + 0.2 * s));
      break;
    }
    case 23: { // The wall cycles through hues.
      const hue = (t * 80) % 360;
      fillBand(ctx, g, `hsla(${hue},90%,60%,0.6)`);
      ctx.lineWidth = Math.max(1.5, bw * 0.18); ctx.strokeStyle = `hsl(${hue},90%,65%)`;
      edgePath(ctx, g, bw * 0.1); ctx.stroke();
      break;
    }
    case 24: { // The walls dim toward the background - they look like they're opening up.
      const s = 0.5 + 0.5 * Math.sin(t * 2);
      fillBand(ctx, g, rgba("#0d1320", 0.35 + 0.5 * s));
      ctx.globalAlpha = 0.4; ctx.lineWidth = Math.max(1, bw * 0.12); ctx.strokeStyle = col;
      edgePath(ctx, g, bw * 0.1); ctx.stroke();
      break;
    }
    case 25: { // Checkerboard squares flash across the wall band.
      const b = bounds(g);
      ctx.save(); bandClip(ctx, g);
      const step = Math.max(6, bw * 0.9), phase = Math.floor(t * 4) % 2;
      let row = 0;
      for (let y = b.t; y < b.b; y += step, row++) {
        let cell = 0;
        for (let x = b.l; x < b.r; x += step, cell++) {
          if ((row + cell + phase) % 2 === 0) { ctx.fillStyle = rgba(col, 0.45); ctx.fillRect(x, y, step, step); }
        }
      }
      ctx.restore();
      break;
    }

    // ===== variations of #24: the walls open up =====
    case 26: { // Full vanish: the wall dims almost entirely toward the background, breathing.
      const s = 0.5 + 0.5 * Math.sin(t * 1.8);
      fillBand(ctx, g, rgba("#0d1320", 0.55 + 0.4 * s));
      break;
    }
    case 27: { // Fade from the inside out: the inner edge opens first, solid toward the outside.
      ctx.save(); bandClip(ctx, g);
      for (let i = 0; i < 6; i++) { edgePath(ctx, g, (i / 6) * bw); ctx.fillStyle = rgba("#0d1320", 0.9 * (1 - i / 6)); ctx.fill(); }
      ctx.restore();
      break;
    }
    case 28: { // Dissolving patches: chunks of wall wink out and back, like it's disintegrating.
      ctx.save(); bandClip(ctx, g);
      const n = Math.max(10, Math.floor(per / 40));
      for (let i = 0; i < n; i++) {
        const s0 = (i / n) * per;
        const ph = (t * 0.5 + fract(Math.sin(i * 3.1) * 99.7)) % 1;
        const a = Math.max(0, Math.sin(ph * Math.PI));
        const p = pointAt(g, s0);
        ctx.fillStyle = rgba("#0d1320", a * 0.92);
        ctx.beginPath(); ctx.arc(p.x + p.nx * bw * 0.5, p.y + p.ny * bw * 0.5, bw * 0.9, 0, TAU); ctx.fill();
      }
      ctx.restore();
      break;
    }
    case 29: { // A faded opening travels around the wall, like a doorway sliding along.
      ctx.save(); bandClip(ctx, g);
      const s0 = (t * per * 0.25) % per;
      for (let k = -14; k <= 14; k++) {
        const p = pointAt(g, s0 + k * bw * 0.6), a = 1 - Math.abs(k) / 15;
        ctx.fillStyle = rgba("#0d1320", a * 0.95);
        ctx.beginPath(); ctx.arc(p.x + p.nx * bw * 0.4, p.y + p.ny * bw * 0.4, bw * 0.8, 0, TAU); ctx.fill();
      }
      ctx.restore();
      break;
    }
    case 30: { // Evenly-spaced gaps open in the wall and slowly drift around.
      ctx.save();
      ctx.lineWidth = bw * 1.1; ctx.strokeStyle = rgba("#0d1320", 0.92);
      ctx.setLineDash([bw * 1.6, bw * 2.6]); ctx.lineDashOffset = -t * bw * 1.5;
      edgePath(ctx, g, bw * 0.5); ctx.stroke();
      ctx.restore();
      break;
    }
    case 31: { // The wall fades while a soft colour glow marks the open edge.
      const s = 0.5 + 0.5 * Math.sin(t * 2);
      fillBand(ctx, g, rgba("#0d1320", 0.4 + 0.4 * s));
      ctx.lineWidth = Math.max(1.5, bw * 0.2); ctx.strokeStyle = rgba(col, 0.55);
      ctx.shadowBlur = bw * 0.8; ctx.shadowColor = col;
      edgePath(ctx, g, bw * 0.12); ctx.stroke();
      break;
    }
    case 32: { // Countdown band: a green field on the inner edge that recedes toward the
      // wall as wall-hack drains, gone when the effect ends. `frac` is the (already eased)
      // shown depth - the map renderer grows it out fast on grab and tracks the slow drain.
      const maxDepth = bw * 1.5;            // depth at full
      const depth = maxDepth * Math.max(0, Math.min(1, frac));
      if (depth <= 0.5) break;
      edgeRegion(ctx, g, 0, depth);         // band from the inner wall edge inward by depth
      ctx.fillStyle = rgba(col, 0.38); ctx.fill("evenodd");
      ctx.lineWidth = Math.max(0.75, bw * 0.125); ctx.strokeStyle = col; // half the front-line width
      edgePath(ctx, g, depth); ctx.stroke(); // crisp front at the receding edge (no glow)
      break;
    }
    // ===== variations of the countdown band (32); all drain with `frac` =====
    case 33: { // Gradient fill: brightest at the wall, fading to the receding front.
      const depth = bw * 1.5 * frac; if (depth <= 0.5) break;
      const steps = 6;
      for (let i = 0; i < steps; i++) { edgeRegion(ctx, g, (i / steps) * depth, ((i + 1) / steps) * depth); ctx.fillStyle = rgba(col, 0.5 * (1 - i / steps)); ctx.fill("evenodd"); }
      break;
    }
    case 34: { // Just the front line receding toward the wall (no fill).
      const depth = bw * 1.5 * frac; if (depth <= 0.5) break;
      ctx.lineWidth = Math.max(1, bw * 0.2); ctx.strokeStyle = col; edgePath(ctx, g, depth); ctx.stroke();
      break;
    }
    case 35: { // Two thin parallel lines at the receding front.
      const depth = bw * 1.5 * frac; if (depth <= 0.5) break;
      ctx.lineWidth = Math.max(0.75, bw * 0.1); ctx.strokeStyle = col;
      edgePath(ctx, g, depth); ctx.stroke();
      edgePath(ctx, g, Math.max(0, depth - bw * 0.35)); ctx.stroke();
      break;
    }
    case 36: { // Full-depth band that just fades out as time runs down.
      const depth = bw * 1.5;
      edgeRegion(ctx, g, 0, depth); ctx.fillStyle = rgba(col, 0.5 * frac); ctx.fill("evenodd");
      ctx.lineWidth = Math.max(0.75, bw * 0.125); ctx.strokeStyle = rgba(col, frac); edgePath(ctx, g, depth); ctx.stroke();
      break;
    }
    case 37: { // Segmented gauge around the perimeter: segments switch off as it drains.
      const N = 24, off = -bw * 0.5, lw = bw * 0.9;
      for (let i = 0; i < N; i++) {
        if ((i + 0.5) / N > frac) continue;
        wallStroke(ctx, g, (i / N) * per + per * 0.006, ((i + 1) / N) * per - per * 0.006, off, lw, rgba(col, 0.8));
      }
      break;
    }
    case 38: { // The wall fills with colour, then empties around the perimeter like a clock.
      wallStroke(ctx, g, 0, per * frac, -bw * 0.5, bw * 0.9, rgba(col, 0.8));
      break;
    }
    case 39: { // Receding band with a glowing front (glow variant).
      const depth = bw * 1.5 * frac; if (depth <= 0.5) break;
      edgeRegion(ctx, g, 0, depth); ctx.fillStyle = rgba(col, 0.3); ctx.fill("evenodd");
      ctx.lineWidth = Math.max(1, bw * 0.18); ctx.strokeStyle = col; ctx.shadowBlur = bw * 0.7; ctx.shadowColor = col;
      edgePath(ctx, g, depth); ctx.stroke();
      break;
    }
    case 40: { // Diagonal stripes inside the receding band.
      const depth = bw * 1.5 * frac; if (depth <= 0.5) break;
      ctx.save(); edgeRegion(ctx, g, 0, depth); ctx.clip("evenodd");
      const b = bounds(g), step = Math.max(4, bw * 0.6), o = (t * 15) % (step * 2), span = b.b - b.t;
      ctx.strokeStyle = rgba(col, 0.6); ctx.lineWidth = step * 0.5;
      for (let d = b.l - span; d < b.r + span; d += step * 2) { ctx.beginPath(); ctx.moveTo(d + o, b.t); ctx.lineTo(d + o - span, b.b); ctx.stroke(); }
      ctx.restore();
      break;
    }
    case 41: { // Dots inside the receding band.
      const depth = bw * 1.5 * frac; if (depth <= 0.5) break;
      ctx.save(); edgeRegion(ctx, g, 0, depth); ctx.clip("evenodd");
      const b = bounds(g), step = Math.max(4, bw * 0.5);
      ctx.fillStyle = rgba(col, 0.6);
      for (let y = b.t; y < b.b; y += step) for (let x = b.l; x < b.r; x += step) { ctx.beginPath(); ctx.arc(x, y, bw * 0.12, 0, TAU); ctx.fill(); }
      ctx.restore();
      break;
    }
    case 42: { // Reverse: the band's base pulls off the wall, opening a gap that grows.
      const maxD = bw * 1.5, d0 = (1 - frac) * maxD, d1 = maxD;
      if (d1 - d0 <= 0.5) break;
      edgeRegion(ctx, g, d0, d1); ctx.fillStyle = rgba(col, 0.38); ctx.fill("evenodd");
      ctx.lineWidth = Math.max(0.75, bw * 0.125); ctx.strokeStyle = col; edgePath(ctx, g, d0); ctx.stroke();
      break;
    }
    case 43: { // Soft feathered front instead of a crisp line.
      const depth = bw * 1.5 * frac; if (depth <= 0.5) break;
      edgeRegion(ctx, g, 0, depth * 0.6); ctx.fillStyle = rgba(col, 0.4); ctx.fill("evenodd");
      const fs = 4;
      for (let i = 0; i < fs; i++) { edgeRegion(ctx, g, depth * 0.6 + (i / fs) * depth * 0.4, depth * 0.6 + ((i + 1) / fs) * depth * 0.4); ctx.fillStyle = rgba(col, 0.4 * (1 - i / fs)); ctx.fill("evenodd"); }
      break;
    }
    case 44: { // Receding band that also pulses brightness.
      const depth = bw * 1.5 * frac; if (depth <= 0.5) break;
      const s = 0.6 + 0.4 * Math.sin(t * 4);
      edgeRegion(ctx, g, 0, depth); ctx.fillStyle = rgba(col, 0.15 + 0.3 * s); ctx.fill("evenodd");
      ctx.lineWidth = Math.max(0.75, bw * 0.125); ctx.strokeStyle = rgba(col, s); edgePath(ctx, g, depth); ctx.stroke();
      break;
    }
    case 45: { // Two-tone: dim fill with a brighter front third.
      const depth = bw * 1.5 * frac; if (depth <= 0.5) break;
      edgeRegion(ctx, g, 0, depth); ctx.fillStyle = rgba(col, 0.22); ctx.fill("evenodd");
      edgeRegion(ctx, g, depth * 0.6, depth); ctx.fillStyle = rgba(col, 0.55); ctx.fill("evenodd");
      break;
    }
    case 46: { // Recede AND fade together.
      const depth = bw * 1.5 * frac; if (depth <= 0.5) break;
      edgeRegion(ctx, g, 0, depth); ctx.fillStyle = rgba(col, 0.15 + 0.35 * frac); ctx.fill("evenodd");
      break;
    }
    case 47: { // Hollow: an outline at the wall edge and at the receding front, no fill.
      const depth = bw * 1.5 * frac; if (depth <= 0.5) break;
      ctx.lineWidth = Math.max(0.75, bw * 0.12); ctx.strokeStyle = col;
      edgePath(ctx, g, 0); ctx.stroke();
      edgePath(ctx, g, depth); ctx.stroke();
      break;
    }
    case 48: { // Colour shifts from green to amber as time runs low.
      const depth = bw * 1.5 * frac; if (depth <= 0.5) break;
      const hue = 20 + 100 * frac; // ~120 green (full) -> ~20 red-amber (low)
      edgeRegion(ctx, g, 0, depth); ctx.fillStyle = `hsla(${hue},85%,55%,0.38)`; ctx.fill("evenodd");
      ctx.lineWidth = Math.max(0.75, bw * 0.125); ctx.strokeStyle = `hsl(${hue},85%,58%)`; edgePath(ctx, g, depth); ctx.stroke();
      break;
    }
    case 49: { // Blinks, and blinks faster the lower it gets.
      const depth = bw * 1.5 * frac; if (depth <= 0.5) break;
      const rate = 3 + (1 - frac) * 11;
      if (Math.sin(t * rate) > 0) { edgeRegion(ctx, g, 0, depth); ctx.fillStyle = rgba(col, 0.42); ctx.fill("evenodd"); }
      break;
    }
    case 50: { // Marching-ants front line on a faint receding fill.
      const depth = bw * 1.5 * frac; if (depth <= 0.5) break;
      edgeRegion(ctx, g, 0, depth); ctx.fillStyle = rgba(col, 0.25); ctx.fill("evenodd");
      ctx.lineWidth = Math.max(1, bw * 0.18); ctx.strokeStyle = col;
      ctx.setLineDash([bw * 0.8, bw * 0.6]); ctx.lineDashOffset = -t * bw * 3;
      edgePath(ctx, g, depth); ctx.stroke(); ctx.setLineDash([]);
      break;
    }
    case 51: { // Tick marks spanning the band from wall to the receding front.
      const depth = bw * 1.5 * frac; if (depth <= 0.5) break;
      edgeRegion(ctx, g, 0, depth); ctx.fillStyle = rgba(col, 0.2); ctx.fill("evenodd");
      ctx.strokeStyle = rgba(col, 0.75); ctx.lineWidth = Math.max(1, bw * 0.12);
      const N = Math.max(16, Math.floor(per / 14));
      for (let i = 0; i < N; i++) { const p = pointAt(g, (i / N) * per); ctx.beginPath(); ctx.moveTo(p.x, p.y); ctx.lineTo(p.x + p.nx * depth, p.y + p.ny * depth); ctx.stroke(); }
      break;
    }
    case 52: { // Subtle: a faint wide fill with a thin bright front. The front finishes a
      // hair INSIDE the wall and fades its alpha over that last stretch, so it dissolves
      // into the wall instead of the final pixel popping off.
      const maxDepth = bw * 1.5;
      const endInset = -Math.min(1.5, bw * 0.35);           // front ends ~1px into the wall
      const depth = endInset + (maxDepth - endInset) * Math.max(0, Math.min(1, frac));
      const a = Math.min(1, (depth - endInset) / Math.max(2, bw * 0.7)); // fade the last stretch
      if (a <= 0.02) break;
      edgeRegion(ctx, g, 0, depth); ctx.fillStyle = rgba(col, 0.15 * a); ctx.fill("evenodd");
      ctx.lineWidth = Math.max(0.75, bw * 0.1); ctx.strokeStyle = rgba(col, 0.85 * a); edgePath(ctx, g, depth); ctx.stroke();
      break;
    }

    default: { // unknown variant: fall back to the neon glow.
      const s = 0.5 + 0.5 * Math.sin(t * 3);
      ctx.lineWidth = Math.max(2, bw * 0.5); ctx.strokeStyle = col;
      ctx.shadowBlur = bw * (0.8 + 2 * s); ctx.shadowColor = col;
      edgePath(ctx, g, bw * 0.4); ctx.stroke();
      break;
    }
  }
  ctx.restore();
}

// Which wall-hack design to draw IN-GAME. Switch with ?wh=1..52 (persists in
// localStorage); default 52 (subtle countdown band: faint fill + thin front).
const OK = (s: string | null): boolean => !!s && /^([1-9]|[1-4][0-9]|5[0-2])$/.test(s);
export function getWallHackVariant(): number {
  return readVariantParam("wh", "whVariant", OK, 52);
}
// Capture ?wh at load so it persists even if navigation strips the URL before a draw.
getWallHackVariant();
